import { forwardRef } from 'react';
import { Linkedin, Mail, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSectionNavigation } from '@/hooks/useSectionNavigation';
import { Button } from '@/components/ui/button';

const navLinks = [
  { href: '#services', label: 'Services' },
  { href: '#solutions', label: 'Solutions' },
  { href: '#why', label: 'Why Yaw' },
  { href: '#contact', label: 'Contact' },
];

export const Footer = forwardRef<HTMLElement>((_, ref) => {
  const goToSection = useSectionNavigation();
  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    goToSection(href);
  };

  return (
    <footer ref={ref} className="py-8 sm:py-10 bg-navy-deep border-t border-white/10">
      <div className="container mx-auto px-5 sm:px-6">
        {/* Top Section - Logo, Tagline, CTA */}
        <div className="text-center mb-8">
          <a href="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="inline-block" aria-label="Back to top">
            <img src="/logo-transparent-160.webp" alt="Yaw CTV" width={160} height={113} loading="lazy" decoding="async" className="h-16 sm:h-20 w-auto mb-3 mx-auto" />
          </a>
          <p className="text-white/80 text-sm mb-1">Strategic Digital Marketing for Government & Business</p>
          <p className="text-white/70 text-xs mb-4">Orlando, Florida • Serving Nationwide</p>
          <Button
            asChild
            size="sm"
            className="bg-orange hover:bg-orange-hover text-white font-semibold transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-orange/25 px-4"
          >
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 uppercase tracking-wide text-[11px] whitespace-nowrap">
              <span>Launch Now</span>
              <ArrowRight size={12} className="shrink-0" aria-hidden="true" />
            </Link>
          </Button>
        </div>

        {/* Links Section - 3 columns on tablet+ */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
          {/* Services */}
          <div className="text-center sm:text-left">
            <h3 className="text-white font-semibold text-sm mb-3">Services</h3>
            <ul className="space-y-1.5 text-sm">
              <li><a href="#services" onClick={(e) => handleNavClick(e, '#services')} className="text-white/80 hover:text-white transition-colors">Paid Media</a></li>
              <li><a href="#services" onClick={(e) => handleNavClick(e, '#services')} className="text-white/80 hover:text-white transition-colors">OTT/CTV Advertising</a></li>
              <li><a href="#services" onClick={(e) => handleNavClick(e, '#services')} className="text-white/80 hover:text-white transition-colors">Programmatic</a></li>
              <li><a href="#services" onClick={(e) => handleNavClick(e, '#services')} className="text-white/80 hover:text-white transition-colors">Social Media</a></li>
            </ul>
          </div>

          {/* Company */}
          <div className="text-center sm:text-left">
            <h3 className="text-white font-semibold text-sm mb-3">Company</h3>
            <ul className="space-y-1.5 text-sm">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <a href={link.href} onClick={(e) => handleNavClick(e, link.href)} className="text-white/80 hover:text-white transition-colors">
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Connect */}
          <div className="text-center sm:text-left">
            <h3 className="text-white font-semibold text-sm mb-3">Connect</h3>
            <div className="flex gap-3 justify-center sm:justify-start">
              <a
                href="https://www.linkedin.com/company/yawdigital"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Yaw CTV on LinkedIn"
                className="text-white/80 hover:text-orange transition-colors inline-flex items-center justify-center min-h-11 min-w-11"
              >
                <Linkedin size={20} aria-hidden="true" />
              </a>
              <a
                href="mailto:ads@yawctv.com"
                aria-label="Email ads@yawctv.com"
                className="text-white/80 hover:text-orange transition-colors inline-flex items-center justify-center min-h-11 min-w-11"
              >
                <Mail size={20} aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>

        <div className="h-px bg-white/10 my-4" />

        <div className="flex flex-col items-center justify-center gap-2 text-center">
          <div className="flex items-center justify-center gap-3 text-xs">
            <Link to="/privacy" className="text-white/80 hover:text-white transition-colors">Privacy Policy</Link>
            <span className="text-white/40" aria-hidden="true">·</span>
            <Link to="/terms" className="text-white/80 hover:text-white transition-colors">Terms of Service</Link>
          </div>
          <p className="text-white/70 text-xs">© 2026 Yaw Media Group. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
});

Footer.displayName = 'Footer';
